import { Facebook, Instagram, Linkedin, Phone, Lock } from "lucide-react";
import { CONSUMER_PAYMENT_METHODS } from "@/lib/commerce";
import { Link } from "@tanstack/react-router";
import elvionLogo from "@/assets/elvion-logo-white.png.asset.json";
import { responsiveImage } from "@/lib/responsive-image";
import { trackEvent } from "@/lib/analytics";
import { COMPANY } from "@/lib/company";
import { ReviewBadgeCompact } from "@/components/site/ReviewBadge";

const elvionLogoResponsive = responsiveImage(elvionLogo.url, [200, 320]);

type FooterLink = { label: string; href: string; hash?: string; external?: boolean; children?: { label: string; href: string }[] };

const COLUMNS: { title: string; links: FooterLink[] }[] = [
  {
    title: "Zelf regelen",
    links: [
      { label: "Account", href: "/auth" },
      { label: "Bestelstatus", href: "/order-status" },
      { label: "Adviesgesprek", href: "/advies" },
      { label: "Offerte opvragen", href: "/offerte-aanvragen" },
      { label: "Onderhoudscontract", href: "/klantenservice", hash: "storing" },
    ],
  },
  {
    title: "Klantenservice",
    links: [
      { label: "B2B (Zakelijk)", href: "/zakelijk" },
      { label: "Contact", href: "/contact" },
      { label: "Werken bij ons", href: "/werken-bij" },
      { label: "Over Elvion", href: "/over-ons" },
      { label: "Dekking", href: "/dekking" },
    ],
  },
  {
    title: "Kennisbank",
    links: [
      { label: "Verlichting", href: "/info/verlichting" },
      { label: "Beveiliging", href: "/info/beveiliging" },
      { label: "Laadpalen", href: "/info/laadpalen" },
      { label: "Thuisbatterij", href: "/info/thuisbatterij" },
      { label: "Netwerken", href: "/info/netwerken" },
    ],
  },
  {
    title: "Bekijk ook",
    links: [
      { label: "Aanbiedingen", href: "/aanbiedingen" },
      { label: "Assortiment", href: "/assortiment" },
      { label: "Groepenkast vervangen", href: "/groepenkast-vervangen" },
      { label: "Storingsdienst", href: "/klantenservice", hash: "storing" },
      { label: "Elektrotechniek", href: "/elektrotechniek" },
      { label: "Beveiligingstechniek", href: "/beveiligingstechniek" },
      { label: "Reviews op Homedeal", href: "https://www.homedeal.nl/pro/Jj8dM/elvion-group-nederland/", external: true },
    ],
  },

];

const MarktplaatsIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M4 17V7h2.5l3.5 5.5L13.5 7H16v10h-2.5v-5L10 17H7.5l-3.5-5v5H4z" />
  </svg>
);

const SOCIALS = [
  { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/elvion-group" },
  { Icon: Facebook, label: "Facebook", href: "https://www.facebook.com/elvion.group" },
  { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/elvion.group" },
  { Icon: MarktplaatsIcon, label: "Marktplaats", href: "https://www.marktplaats.nl/smb-profile/profile/58197191" },
];

const PAYMENT_SUMMARY = CONSUMER_PAYMENT_METHODS.map((m) => `· ${m.name}`).join(" ");

export function Footer() {
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="mx-auto max-w-7xl px-4 pt-[37px] pb-10 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-4">
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="font-display text-lg font-extrabold text-white">
                {col.title}
              </h3>
              <ul className="mt-5 space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-white/75 transition-colors hover:text-orange"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        hash={link.hash}
                        className="text-sm text-white/75 transition-colors hover:text-orange"
                      >
                        {link.label}
                      </Link>
                    )}
                    {link.children && (
                      <ul className="mt-2 ml-4 space-y-2 border-l border-white/15 pl-3">
                        {link.children.map((child) => (
                          <li key={child.label}>
                            <Link
                              to={child.href}
                              className="text-sm text-white/60 transition-colors hover:text-orange"
                            >
                              {child.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="my-10 h-px w-full bg-white/15" />

        <div className="mb-10 grid gap-4 rounded-md border border-white/15 bg-white/5 p-4 text-sm text-white/75 md:grid-cols-4">
          <div>
            <p className="font-semibold text-white">Verkoper</p>
            <p className="mt-1">{COMPANY.name}</p>
          </div>
          <div>
            <p className="font-semibold text-white">Bedrijfsgegevens</p>
            <p className="mt-1">KvK {COMPANY.kvk} · BTW {COMPANY.btw}</p>
          </div>
          <div>
            <p className="font-semibold text-white">Veilig bestellen</p>
            <p className="mt-1">SSL beveiligd</p>
          </div>
          <div>
            <p className="font-semibold text-white">Beleid</p>
            <p className="mt-1">14 dagen bedenktijd · wettelijke garantie</p>
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div className="flex-1">
            <div className="flex items-center">
              <img
                src={elvionLogoResponsive?.src ?? elvionLogo.url}
                srcSet={elvionLogoResponsive?.srcSet}
                sizes="200px"
                alt="Elvion Group Nederland logo"
                width={1204}
                height={328}
                className="h-10 w-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="mt-4">
              <ReviewBadgeCompact />
            </div>
            <p className="mt-3 text-sm text-white/70">
              © 2026 Elvion Group Nederland. Alle rechten voorbehouden.
            </p>
            <p className="mt-1 text-xs text-white/50">
              {COMPANY.address}, {COMPANY.postal} · KvK {COMPANY.kvk} · BTW {COMPANY.btw} · {COMPANY.phone} · {COMPANY.email}
            </p>
            <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/75">
              {[
                { label: "Algemene voorwaarden", href: "/voorwaarden" },
                { label: "Privacystatement", href: "/privacy" },
                { label: "Cookiestatement", href: "/cookies" },
                { label: "Disclaimer", href: "/disclaimer" },
                { label: "Bedenktijd", href: "/bedenktijd" },
                { label: "Retourbeleid", href: "/retourbeleid" },
                { label: "Klachten & geschillen", href: "/klachten" },
                { label: "Verzending & levering", href: "/verzendbeleid" },
                { label: "Garantie", href: "/garantie" },
                { label: "Betaalmethoden", href: "/betaalmethoden" },
                { label: "Blog", href: "/blog" },
                { label: "FAQ", href: "/klantenservice", hash: "faq" },
              ].map((item) => (
                <li key={item.label}>
                  <Link to={item.href} hash={item.hash} className="underline-offset-4 hover:text-orange hover:underline">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col items-start gap-4 md:items-end">
            <p className="text-sm font-semibold text-white">Volg ons via social media</p>
            <div className="flex items-center gap-3">
              {SOCIALS.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  onClick={() =>
                    trackEvent("social_click", {
                      social_network: label.toLowerCase(),
                      social_target: href,
                      social_section: "footer",
                      page_path: typeof window !== "undefined" ? window.location.pathname : "",
                    })
                  }
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-primary transition-colors hover:bg-orange hover:text-white"
                >
                  <Icon className="h-4 w-4" strokeWidth={2.4} />
                </a>
              ))}
              <a
                href="tel:+31850806695"
                aria-label="Bel ons: 085 080 66 95"
                onClick={() =>
                  trackEvent("contact_click", {
                    contact_method: "phone",
                    contact_target: "+31850806695",
                    contact_section: "footer",
                    page_path: typeof window !== "undefined" ? window.location.pathname : "",
                  })
                }
                className="flex h-10 w-10 items-center justify-center rounded-full bg-orange text-white transition-colors hover:bg-[var(--orange-hover)]"
              >
                <Phone className="h-4 w-4" strokeWidth={2.4} />
              </a>
            </div>
            <div className="mt-2 flex flex-col items-start gap-2 md:items-end">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/70 md:justify-end">
                <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> SSL beveiligd</span>
                <span>{PAYMENT_SUMMARY}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
